import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, AlertTriangle, RefreshCw, FileText, ArrowRight, Clock } from "lucide-react";
import { resolveBillStatus } from "@/lib/statusResolvers";
import { usePermission } from "@/lib/usePermission";
import TollBillDetailModal from "@/components/TollBillDetailModal";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) { if (!s) return "—"; return String(s).slice(0, 10); }

/**
 * Phase TOLLBILL-P5d (2026-07-10) — Reusable compact toll BILLS list
 * embedded on rental-detail + customer-detail pages.
 *
 * Bills are the first-class entity (P5a). This is the bill-scoped
 * counterpart of ``TollTransactionsList`` (which now serves as a
 * secondary drill-down surface).
 *
 * Summary tiles: Unbilled bills / Disputed bills / Invoiced bills /
 * Total unbilled $ (sum of ``total_due_cents`` on unbilled bills).
 *
 * Filter props: pass ``rentalId`` on rental detail, ``customerId`` on
 * customer detail. Both may be passed together.
 *
 * A "View child transactions →" link at the bottom opens the
 * transactions surface (``/admin/tolls``) pre-filtered by the same id
 * for granular audit.
 */
export default function TollBillsList({
    rentalId,
    customerId,
    testIdPrefix = "toll-bills-list",
    emptyText = "No toll bills.",
}) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detailBillId, setDetailBillId] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");
    const canManage = hasPermission("Tolls", "Manage");

    const load = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (rentalId) params.rental_id = rentalId;
            if (customerId) params.customer_id = customerId;
            const { data } = await api.get("/admin/tolls/bills", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    }, [rentalId, customerId, canView]);

    useEffect(() => { load(); }, [load]);

    const summary = useMemo(() => {
        let unbilledCount = 0, unbilledCents = 0, disputedCount = 0, invoicedCount = 0;
        for (const b of items) {
            if (b.status === "invoiced") invoicedCount++;
            if (b.status === "disputed" || b.dispute_flag) disputedCount++;
            if (b.status === "matched" && !b.dispute_flag && !b.invoice_id) {
                unbilledCount++;
                unbilledCents += Number(b.total_due_cents || 0);
            }
        }
        return { unbilledCount, unbilledCents, disputedCount, invoicedCount };
    }, [items]);

    if (permReady && !canView) return null;

    // Drill-down link into the transactions surface, pre-filtered.
    const txnDrillHref = (() => {
        const params = new URLSearchParams();
        if (customerId) params.set("customer_id", customerId);
        if (rentalId) params.set("rental_id", rentalId);
        const qs = params.toString();
        return qs ? `/admin/tolls?${qs}` : "/admin/tolls";
    })();

    return (
        <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
            {/* Summary strip — 4 bill-level counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryTile
                    label="Unbilled bills"
                    value={summary.unbilledCount}
                    subline={fmtMoney(summary.unbilledCents)}
                    tone="blue"
                    testId={`${testIdPrefix}-summary-unbilled`}
                />
                <SummaryTile
                    label="Disputed"
                    value={summary.disputedCount}
                    tone={summary.disputedCount > 0 ? "red" : "slate"}
                    testId={`${testIdPrefix}-summary-disputed`}
                />
                <SummaryTile
                    label="Invoiced"
                    value={summary.invoicedCount}
                    tone="emerald"
                    testId={`${testIdPrefix}-summary-invoiced`}
                />
                <SummaryTile
                    label="Total bills"
                    value={total}
                    tone="slate"
                    testId={`${testIdPrefix}-summary-total`}
                />
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                        <p className="text-xs text-slate-500">
                            Showing {items.length} of {total} bill{total === 1 ? "" : "s"}
                        </p>
                        <Button variant="ghost" size="sm" onClick={load} title="Refresh" data-testid={`${testIdPrefix}-refresh`}>
                            <RefreshCw size={14} />
                        </Button>
                    </div>
                    {loading && <p className="p-6 text-sm text-slate-500" data-testid={`${testIdPrefix}-loading`}>Loading…</p>}
                    {!loading && items.length === 0 && (
                        <p className="p-8 text-sm text-center text-slate-500" data-testid={`${testIdPrefix}-empty`}>{emptyText}</p>
                    )}
                    {!loading && items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm table-fixed">
                                <colgroup>
                                    <col className="w-[110px]" />  {/* Bill # (mono, short) */}
                                    <col className="w-[86px]" />   {/* Date */}
                                    <col className="w-[86px]" />   {/* Due */}
                                    <col />                         {/* Authority — flex */}
                                    <col className="w-[80px]" />   {/* Tolls */}
                                    <col className="w-[86px]" />   {/* Total */}
                                    <col className="w-[104px]" />  {/* Status + badges */}
                                </colgroup>
                                <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                    <tr>
                                        <th className="text-left py-2 px-2">Bill #</th>
                                        <th className="text-left py-2 px-2">Date</th>
                                        <th className="text-left py-2 px-2">Due</th>
                                        <th className="text-left py-2 px-2">Authority</th>
                                        <th className="text-right py-2 px-2">Tolls</th>
                                        <th className="text-right py-2 px-2">Total</th>
                                        <th className="text-left py-2 px-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {items.map((b) => {
                                        const st = resolveBillStatus(b);
                                        return (
                                            <tr
                                                key={b.bill_id}
                                                data-testid={`${testIdPrefix}-row-${b.bill_id}`}
                                                onClick={() => { setDetailBillId(b.bill_id); setDetailOpen(true); }}
                                                className="hover:bg-slate-50/50 cursor-pointer"
                                                title={`Bill ${b.bill_id}`}
                                            >
                                                <td className="py-2 px-2 overflow-hidden">
                                                    <div
                                                        className="text-blue-900 font-mono text-xs truncate"
                                                        title={b.bill_id}
                                                        data-testid={`${testIdPrefix}-open-${b.bill_id}`}
                                                    >
                                                        {b.bill_id}
                                                    </div>
                                                </td>
                                                <td className="py-2 px-2 text-slate-700 whitespace-nowrap">{fmtDate(b.bill_date)}</td>
                                                <td className="py-2 px-2 whitespace-nowrap">
                                                    <span className={st.isOverdue ? "text-red-700 font-semibold inline-flex items-center gap-1" : "text-slate-700"}>
                                                        {st.isOverdue && <Clock size={11} />}
                                                        {fmtDate(b.due_date)}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-2 overflow-hidden">
                                                    <div className="truncate text-slate-800" title={b.authority || ""}>{b.authority || "—"}</div>
                                                    {b.document_type && (
                                                        <div className="text-[10.5px] text-slate-500 mt-0.5 truncate" title={b.document_type}>{b.document_type}</div>
                                                    )}
                                                </td>
                                                <td className="py-2 px-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtMoney(b.toll_amount_cents)}</td>
                                                <td className="py-2 px-2 text-right font-semibold tabular-nums whitespace-nowrap">{fmtMoney(b.total_due_cents)}</td>
                                                <td className="py-2 px-2">
                                                    <div className="flex items-center gap-1 flex-nowrap">
                                                        <span
                                                            className={`inline-flex items-center rounded-full ring-1 px-2 py-0.5 text-[11px] font-semibold ${st.tone}`}
                                                            data-testid={`${testIdPrefix}-status-${b.bill_id}`}
                                                        >
                                                            {st.label}
                                                        </span>
                                                        {b.has_attachment && (
                                                            <FileText size={12} className="text-slate-500 shrink-0" title="PDF attachment available" data-testid={`${testIdPrefix}-attach-${b.bill_id}`} />
                                                        )}
                                                        {b.dispute_flag && st.code !== "disputed" && (
                                                            <AlertTriangle size={12} className="text-red-600 shrink-0" title="Dispute flag set" />
                                                        )}
                                                        {b.invoice_id && (
                                                            <Link
                                                                to={`/admin/invoices/${b.invoice_id}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-[11px] text-blue-900 hover:underline"
                                                            >
                                                                invoice
                                                            </Link>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Drill into transactions surface */}
            {!loading && items.length > 0 && (
                <div className="flex justify-end">
                    <Link
                        to={txnDrillHref}
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-900 hover:underline"
                        data-testid={`${testIdPrefix}-child-txns-link`}
                    >
                        View child transactions <ArrowRight size={12} />
                    </Link>
                </div>
            )}

            <TollBillDetailModal
                open={detailOpen}
                onOpenChange={setDetailOpen}
                billId={detailBillId}
                canManage={canManage}
                onChanged={load}
            />
        </div>
    );
}

const TONE_MAP = {
    blue: "bg-blue-50 text-blue-900 border-blue-200",
    red: "bg-red-50 text-red-800 border-red-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    slate: "bg-slate-50 text-slate-900 border-slate-200",
};

function SummaryTile({ label, value, subline, tone = "slate", testId }) {
    return (
        <div
            className={`rounded-md border px-3 py-2.5 ${TONE_MAP[tone] || TONE_MAP.slate}`}
            data-testid={testId}
        >
            <p className="text-[11px] uppercase tracking-wider opacity-80">{label}</p>
            <p className="text-2xl font-display font-bold tabular-nums leading-tight mt-0.5" data-testid={`${testId}-value`}>{value}</p>
            {subline && <p className="text-[11px] tabular-nums opacity-80 mt-0.5" data-testid={`${testId}-subline`}>{subline}</p>}
        </div>
    );
}
